import { ArrowRight, ShieldCheck, UserCheck } from 'lucide-react';
import {
  type RequisitionLineDto,
  type RequisitionTransitionDto,
} from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * KORXONANING QARORI — hujjatdagi eng ko'p savol tug'diradigan joy.
 *
 * "Nega 23 emas, 10?" degan savol jadval ustunidagi yolg'iz sondan javob
 * olmaydi. Javob uch bo'lakdan iborat: qancha so'ralgan edi, KIM qancha
 * berdi va NEGA. Uchalasi hujjatning turli joylarida sochilib yotsa (son
 * — jadvalda, ism — tarixda, sabab — sichqoncha ostidagi izohda), odam
 * ularni o'zi yig'ishga majbur bo'ladi va ko'pincha yig'maydi — telefon
 * qiladi.
 *
 * Shuning uchun ular BITTA blokda va faqat korxona haqiqatan KESGANDA
 * chiziladi: hamma satr to'liq tasdiqlangan bo'lsa, ayta*digan* narsa yo'q
 * va blok shovqin bo'lardi.
 */

const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** "27.08.2026 15:54" — qaror qachon qabul qilingani. */
const formatMoment = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export interface OrgDecisionPanelProps {
  lines: readonly RequisitionLineDto[];
  transitions: readonly RequisitionTransitionDto[];
  nameOf: (nomenclatureId: string) => { name: string; code: string; unit?: string } | undefined;
}

export const OrgDecisionPanel = ({ lines, transitions, nameOf }: OrgDecisionPanelProps) => {
  /* Faqat KESILGAN satrlar: to'liq tasdiqlangani hujjatdagi odatiy hol va
     uni takrorlash asosiy xabarni ko'mib qo'yardi. */
  const cut = lines.filter(
    (line) => line.orgApprovedQty !== null && line.orgApprovedQty < line.requestedQty,
  );
  if (cut.length === 0) return null;

  /* Qarorni KIM qabul qilgani — tarixdagi `ORG_APPROVED` yozuvidan.
     Oxirgisi olinadi: talabnoma rad etilib qaytarilgan bo'lsa, amaldagi
     qaror eng keyingisi. */
  const step = [...transitions].reverse().find((row) => row.toStatus === 'ORG_APPROVED');

  return (
    <section
      className={cn(
        'flex flex-col gap-3 rounded-[var(--radius-lg)] border border-warn/30',
        'border-l-4 border-l-warn bg-warn-soft p-4',
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <span
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center',
            'rounded-[var(--radius-md)] bg-warn text-[var(--color-warn-contrast)]',
          )}
        >
          <ShieldCheck size={17} />
        </span>
        <h2 className="text-sm font-semibold text-text">
          {t('requisition.orgDecisionTitle', { count: cut.length })}
        </h2>
      </header>

      {/* KIM va QACHON — savolning yarmi shu. Ism tarixda ham bor, lekin
          u yerda o'nlab yozuv orasida turadi va uni izlash kerak bo'ladi. */}
      {step === undefined ? null : (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-muted">
          <UserCheck size={13} aria-hidden className="shrink-0" />
          <span className="font-medium text-text">
            {step.actorName ?? t('requisition.timelineSystem')}
          </span>
          {step.actorPosition === null ? null : <span>· {step.actorPosition}</span>}
          <span className="tabular">· {formatMoment(step.createdAt)}</span>
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {cut.map((line) => {
          const info = nameOf(line.nomenclatureId);
          const unit = info?.unit === undefined ? '' : unitText(info.unit as never);
          const granted = line.orgApprovedQty ?? 0;
          return (
            <li
              key={line.id}
              className={cn(
                'flex flex-col gap-1.5 rounded-[var(--radius-md)] border border-warn/20',
                'bg-surface px-3 py-2.5',
              )}
            >
              <span className="min-w-0 text-sm font-medium text-balance text-text">
                {info?.name ?? `#${line.nomenclatureId}`}
              </span>

              {/* SO'RALGAN → BERILGAN. Ikki son yonma-yon turgandagina
                  farq o'qiladi; yolg'iz "10" hech narsa demaydi. */}
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="tabular text-sm text-text-faint line-through">
                  {qtyText(line.requestedQty)} {unit}
                </span>
                <ArrowRight size={13} aria-hidden className="shrink-0 text-text-faint" />
                <span
                  className={cn(
                    'tabular text-lg leading-none font-semibold',
                    granted === 0 ? 'text-danger' : 'text-warn',
                  )}
                >
                  {qtyText(granted)}
                </span>
                <span className="text-xs text-text-muted">{unit}</span>
              </span>

              {line.orgNote === null ? null : (
                <span className="text-xs leading-5 text-text">{line.orgNote}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
